import React, { useCallback, useEffect, useRef } from "react";
import toast from "react-hot-toast";
import { API_BASE_URL } from "../../services/api";
import { Dataset } from "../../services/consentService";
import { NexBeSignAuthResponse } from "../../services/omniAuthService";

// 환경변수로 Mock 모드 제어
const USE_MOCK_SIGNATURE = import.meta.env.VITE_USE_MOCK_SIGNATURE === "true";

interface InstitutionSignatureData {
  provideInstitutionNo: string;
  provideInstitutionName: string;
  datasets: Dataset[];
}

interface OmniAuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAllSignaturesComplete?: (
    results: Array<{
      institutionNo: string;
      institutionName: string;
      authResult: {
        parsedData: NexBeSignAuthResponse;
        txId: string;
        userIp: string;
      };
    }>
  ) => void;
  institutions: InstitutionSignatureData[];
  text: string;
  serviceId?: string;
  nexbeUiUrl?: string;
}

const OmniAuthModal: React.FC<OmniAuthModalProps> = ({
  isOpen,
  onClose,
  onAllSignaturesComplete,
  institutions,
  text,
  serviceId = "test-1",
  nexbeUiUrl = "https://nxbs-tst.nexonesoft.com:20443/sign/v2",
}) => {
  const windowRef = useRef<Window | null>(null);
  const popupCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // 팝업 체크 중지 함수
  const stopPopupCloseCheck = useCallback(() => {
    if (popupCheckIntervalRef.current) {
      clearInterval(popupCheckIntervalRef.current);
      popupCheckIntervalRef.current = null;
    }
  }, []);

  // Mock 서명 데이터 생성 함수 (USE_MOCK_SIGNATURE=true 일 때만 사용)
  const generateMockSignatureResults = useCallback(() => {
    const now = new Date();
    const agreementDate = now.toISOString().split("T")[0];
    const agreementDateTime = now.toISOString().replace("T", " ").split(".")[0];

    return institutions.map((institution, index) => {
      const mockTxId = `mock-tx-${Date.now()}-${index}`;
      const mockSiteTxId = `${agreementDate.replace(/-/g, "")}${Math.random()
        .toString(36)
        .substring(2, 15)}`;

      const mockSignedContent = btoa(
        `MOCK_SIGNATURE_DATA_${mockTxId}_${institution.provideInstitutionNo}`
      ).repeat(10);

      const signedContent: NexBeSignAuthResponse = {
        ca_code: "ktpass",
        ca_std_yn: true,
        code: 200,
        encrypted: false,
        message: "서명 완료",
        notified_at: now.toISOString(),
        nxbs_tx_id: mockTxId,
        options: null,
        rejected_at: null,
        requested_at: now.toISOString(),
        result: null,
        signed_at: now.toISOString(),
        signed_content_list: [
          {
            content_id: `mock-content-${index}`,
            signed_content: mockSignedContent,
            signed_datetime: agreementDateTime,
          },
        ],
        site_tx_id: mockSiteTxId,
        status: "signed",
        tx_id: mockTxId,
        viewed_at: now.toISOString(),
      };

      return {
        institutionNo: institution.provideInstitutionNo,
        institutionName: institution.provideInstitutionName,
        authResult: {
          parsedData: signedContent,
          txId: mockTxId,
          userIp: "127.0.0.1",
        },
      };
    });
  }, [institutions]);

  // 팝업에서 메시지를 받기 위한 이벤트 리스너
  const handleMessage = useCallback(
    (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return;
      }

      if (event.data.type === "NEXBE_AUTH_SUCCESS") {
        const { submitJson, nxbsTxId, userIp } = event.data;

        try {
          const parsedData = JSON.parse(submitJson) as NexBeSignAuthResponse;

          // signed_content_list의 개수와 institutions 개수가 일치하는지 확인
          if (parsedData.signed_content_list.length !== institutions.length) {
            console.error(
              "서명 개수 불일치:",
              parsedData.signed_content_list.length,
              "vs",
              institutions.length
            );
            toast.error("서명 개수가 일치하지 않습니다.");
            onClose();
            return;
          }

          // 각 기관에 대응하는 서명 결과 매핑
          const allSignatures = institutions.map((institution, index) => ({
            institutionNo: institution.provideInstitutionNo,
            institutionName: institution.provideInstitutionName,
            authResult: {
              parsedData: {
                ...parsedData,
                // 해당 기관의 서명만 포함
                signed_content_list: [parsedData.signed_content_list[index]],
              },
              txId: nxbsTxId,
              userIp,
            },
          }));

          toast.success("모든 기관의 전자서명이 완료되었습니다.");

          // 팝업 체크 중지
          stopPopupCloseCheck();

          // 팝업 닫기
          if (windowRef.current && !windowRef.current.closed) {
            windowRef.current.close();
            windowRef.current = null;
          }

          // 모든 서명 완료 콜백 호출
          if (onAllSignaturesComplete) {
            onAllSignaturesComplete(allSignatures);
          }
        } catch (error) {
          console.error("인증 결과 파싱 실패:", error);
          toast.error("인증 결과 처리 중 오류가 발생했습니다.");
          onClose();
        }
      } else if (event.data.type === "NEXBE_AUTH_CLOSE") {
        stopPopupCloseCheck();
        toast.error("전자서명이 취소되었습니다.");
        if (windowRef.current && !windowRef.current.closed) {
          windowRef.current.close();
          windowRef.current = null;
        }
      } else if (event.data.type === "NEXBE_AUTH_ERROR") {
        stopPopupCloseCheck();
        toast.error(event.data.message || "인증 중 오류가 발생했습니다.");
        if (windowRef.current && !windowRef.current.closed) {
          windowRef.current.close();
          windowRef.current = null;
        }
      }
    },
    [institutions, onAllSignaturesComplete, onClose, stopPopupCloseCheck]
  );

  // NexBe Sign SDK HTML 생성 함수
  const createPopupContent = useCallback(
    (
      serviceId: string,
      nexbeUiUrl: string,
      nexbeSvUrl: string,
      text: string,
      institutionCount: number
    ) => {
      // 기관 수만큼 동일한 서명 텍스트 생성
      const digitalSignArray = Array.from({ length: institutionCount }, () => ({
        content_id: "",
        type: "TEXT",
        content: text,
      }));

      return `
<!DOCTYPE html>
<html lang="ko">
  <head>
    <meta charset="UTF-8" />
    <meta http-equiv="X-UA-Compatible" content="IE=edge" />
    <meta name="viewport" content="width=device-width,initial-scale=1" />
    <title>NexBe Sign - 마이데이터 인증</title>
    <link rel="stylesheet" href="${nexbeUiUrl}/app.css" />
    <script src="${nexbeUiUrl}/sdk.js"></script>
    <script src="${nexbeUiUrl}/app.js"></script>
  </head>

  <body>
    <div id="dsh-root"></div>
    
    <script>
      // 설정 변수
      var mydataServiceId = '${serviceId}';
      var ipAddress = '';
      var ca_code = '';
      var submit_json = '';
      var enc_data = '';
      var nxbs_tx_id = '';
      
      // 옵션
      var ipOption = true;
      var encrypted = false;
      var useSdk = true;
      var aosDeepLink = false;
      var iosDeepLink = false;
      var callPolicy = true;
      var requestTitle = '인증';
      
      // URL 세팅
      var NEXBE_UI_URL = '${nexbeUiUrl}';
      var NEXBE_SV_URL = '${nexbeSvUrl}';
      
      // 전자서명 서명원문 (기관 수만큼)
      var digitalSign = ${JSON.stringify(digitalSignArray)};

      // IP 랜덤 생성 함수
      function generateRandomIP() {
        const getRandomNumber = () => Math.floor(Math.random() * 255) + 1;
        const segment1 = getRandomNumber();
        const segment2 = getRandomNumber();
        const segment3 = getRandomNumber();
        const segment4 = getRandomNumber();
        return segment1 + '.' + segment2 + '.' + segment3 + '.' + segment4;
      }

      // 공인 IP 주소 요청
      function getClientIP(option) {
        if (option) {
          var xhr = new XMLHttpRequest();
          xhr.open('GET', 'https://api64.ipify.org?format=json', true);
          xhr.onreadystatechange = function () {
            if (xhr.readyState === 4 && xhr.status === 200) {
              var res = JSON.parse(xhr.responseText);
              ipAddress = res.ip.length > 20 ? generateRandomIP() : res.ip;
              DSH_SDK.CONFIG.USERIP = ipAddress;
            }
          };
          xhr.onerror = function (error) {
            console.error(error);
          };
          xhr.send();
        }
      }

      getClientIP(ipOption);

      // 화면 렌더링 mode 4: 마이데이터
      function clickBtn(mode) {
        DSH.option.MODE = mode;
        DSH_SDK.CONFIG.MODE = DSH.option.MODE;

        // 토큰 요청
        DSH_SDK.REQUEST.TOKEN(
          useSdk
            ? {}
            : {
                service_id: mydataServiceId
              }
        );
      }

      // 토큰 응답
      DSH_SDK.CALLBACK.RESPONSE.TOKEN = function (data) {
        if (!useSdk) {
          data.service_id = mydataServiceId;
        }
        DSH.SET_COMMON_DATA(data);
        DSH.SET_DIGITAL_SIGN(digitalSign);
        DSH.SET_USER_JSON({
          name: '',
          phone: '',
          birthday: '',
          telcoType: '',
          countryCode: '',
          rrn1: '',
          rrn2: '',
          gender: ''
        });
        
        DSH.SET_SERVICE_JSON({
          launcher: ["PUSH"],
          request_display: []
        });
        
        if (callPolicy) {
          DSH.SET_POLICY().then(function(res) {
            if (res) {
              DSH.POPUP();
            }
          });
        } else {
          DSH.POPUP();
        }
      };

      // VIEW 설정
      DSH.option.VIEW.TYPE = 'BIRTH';
      DSH.option.VIEW.LISTMODE = 1;
      DSH.option.VIEW.QR_TIMEOUT = 90000;
      DSH.option.VIEW.ANONYMOUS = false;

      // RESOURCE 설정
      DSH.option.RESOURCE.ROOT_PATH = NEXBE_UI_URL;
      DSH.option.RESOURCE.POLICY_ROOT_PATH = NEXBE_UI_URL;
      DSH.option.RESOURCE.POLICY1.PATH = '/policy/policy1.html';
      DSH.option.RESOURCE.POLICY2.PATH = '/policy/policy2.html';
      DSH.option.RESOURCE.POLICY3.PATH = '/policy/policy3.html';
      DSH.option.RESOURCE.REQUESTTITLE = requestTitle;
      DSH.option.RESOURCE.BANNER_PATH = [];

      // DEEPLINK 설정
      DSH.option.DEEPLINK.AOS = aosDeepLink;
      DSH.option.DEEPLINK.IOS = iosDeepLink;
      DSH.option.DEEPLINK.INTENT = false;

      // DSH_SDK 옵션 설정
      DSH.SET_SDK(DSH_SDK);
      DSH_SDK.CONFIG.DSH_API_URL = NEXBE_SV_URL;
      DSH_SDK.CONFIG.SIGN_API_URL = NEXBE_SV_URL;

      // URL 수정
      DSH_SDK.URL.SDK_TOKEN_URL = '/signature/token';
      DSH_SDK.URL.SDK_PROVIDER_LIST_URL = '/signature/list';
      DSH_SDK.URL.SDK_KEY_URL = '/signature/key';
      DSH_SDK.URL.SDK_AUTH_REQUEST = '/signature/request';
      DSH_SDK.URL.SDK_AUTH_RESULT = '/signature/result';
      DSH_SDK.URL.SDK_KEY_EXCHANGE_URL = '/signature/keyExchange';

      DSH_SDK.CONFIG.USESDK = useSdk;
      DSH_SDK.CONFIG.ENCRYPT = encrypted;

      // 닫기 이벤트 감지 및 서명 검증 요청
      DSH.option.CALLBACK.CLOSE_POPUP = function () {
        if (submit_json) {
          if (window.opener && window.opener !== window) {
            window.opener.postMessage({
              type: 'NEXBE_AUTH_SUCCESS',
              submitJson: submit_json,
              userIp: ipAddress,
              nxbsTxId: nxbs_tx_id
            }, window.opener.location.origin);
            window.close();
          }
        } else {
          // 인증 실패 또는 취소
          if (window.opener && window.opener !== window) {
            window.opener.postMessage({
              type: 'NEXBE_AUTH_CLOSE'
            }, window.opener.location.origin);
          }
          window.close();
        }
      };

      // 인증 요청-요청전문
      DSH.option.API.CALLBACK.REQUEST.AUTH_REQUEST = function (res) {
        var json = JSON.parse(res);
        ca_code = json.ca_code;
        nxbs_tx_id = json.nxbs_tx_id;
      };

      // 인증 요청-응답전문
      DSH.option.API.CALLBACK.RESPONSE.AUTH_REQUEST = function (res) {
        enc_data = JSON.parse(res).enc_data;
      };

      // 인증확인-응답전문
      DSH.option.API.CALLBACK.RESPONSE.AUTH_RESULT = function (res) {
        var json = JSON.parse(res);
        if (json.code === 200) {
          submit_json = res;
        }
      };

      // 안드로이드 딥링크 URL 콜백
      DSH.option.CALLBACK.AOS = function (res) {
      };

      // IOS 딥링크 URL 콜백
      DSH.option.CALLBACK.IOS = function (res) {
      };

      // 페이지 로드 완료 후 자동 실행
      window.addEventListener('load', function() {
        // 마이데이터 모드로 시작
        clickBtn(4);
      });

      // 에러 핸들링
      window.addEventListener('error', function(event) {
        console.error('Window error:', event.error);
        if (window.opener && window.opener !== window) {
          window.opener.postMessage({
            type: 'NEXBE_AUTH_ERROR',
            message: event.error?.message || '인증 중 오류가 발생했습니다.'
          }, window.opener.location.origin);
        }
      });
    </script>
  </body>
</html>
      `;
    },
    []
  );

  // 팝업 닫힘 감지 함수
  const startPopupCloseCheck = useCallback(() => {
    // 기존 인터벌 정리
    if (popupCheckIntervalRef.current) {
      clearInterval(popupCheckIntervalRef.current);
      popupCheckIntervalRef.current = null;
    }

    // 500ms마다 팝업 상태 체크
    popupCheckIntervalRef.current = setInterval(() => {
      if (windowRef.current && windowRef.current.closed) {
        // 팝업이 닫혔음을 감지
        clearInterval(popupCheckIntervalRef.current!);
        popupCheckIntervalRef.current = null;
        windowRef.current = null;

        toast.error("전자서명 팝업이 닫혔습니다.");
      }
    }, 500);
  }, []);

  // 전자서명 시작 함수
  const handleStartSignature = useCallback(() => {
    if (institutions.length === 0) {
      toast.error("서명할 기관 정보를 찾을 수 없습니다.");
      return;
    }

    // 기존 팝업이 있으면 먼저 닫기
    if (windowRef.current && !windowRef.current.closed) {
      windowRef.current.close();
      windowRef.current = null;
    }

    // 새 팝업 열기
    const popupFeatures = "width=400,height=626,scrollbars=yes,resizable=yes";
    const popupName = `nexbe_auth_popup_${Date.now()}`;
    const newPopup = window.open("", popupName, popupFeatures);

    if (!newPopup) {
      toast.error(
        "팝업이 차단되었습니다. 브라우저 설정에서 팝업을 허용해 주세요.",
        { duration: 5000 }
      );
      return;
    }

    windowRef.current = newPopup;

    try {
      windowRef.current.document.write(
        createPopupContent(
          serviceId,
          nexbeUiUrl,
          API_BASE_URL,
          text,
          institutions.length
        )
      );
      windowRef.current.document.close();

      // 팝업 닫힘 감지 시작
      startPopupCloseCheck();
    } catch (error) {
      console.error("팝업 콘텐츠 작성 실패:", error);
      toast.error("팝업을 열 수 없습니다.");
    }
  }, [
    institutions,
    serviceId,
    nexbeUiUrl,
    text,
    createPopupContent,
    startPopupCloseCheck,
  ]);

  // 메시지 리스너 등록
  useEffect(() => {
    if (isOpen) {
      window.addEventListener("message", handleMessage);
      return () => {
        window.removeEventListener("message", handleMessage);
      };
    }
  }, [isOpen, handleMessage]);

  // 모달이 닫힐 때 정리
  useEffect(() => {
    if (!isOpen) {
      stopPopupCloseCheck();
      if (windowRef.current && !windowRef.current.closed) {
        windowRef.current.close();
        windowRef.current = null;
      }
    }
  }, [isOpen, stopPopupCloseCheck]);

  // Mock 모드: 팝업 없이 즉시 서명 완료 결과 반환 (VITE_USE_MOCK_SIGNATURE=true)
  useEffect(() => {
    if (isOpen && USE_MOCK_SIGNATURE && institutions.length > 0) {
      console.warn(
        "⚠️ Signature Mock 모드가 활성화되었습니다. (VITE_USE_MOCK_SIGNATURE=true)"
      );
      const timer = setTimeout(() => {
        const mockResults = generateMockSignatureResults();
        toast.success("모든 기관의 전자서명이 완료되었습니다.", {
          duration: 3000,
        });
        if (onAllSignaturesComplete) {
          onAllSignaturesComplete(mockResults);
        }
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [
    isOpen,
    institutions.length,
    generateMockSignatureResults,
    onAllSignaturesComplete,
  ]);

  // 모달이 열릴 때 자동으로 팝업 열기 (Mock 모드가 아닐 때만)
  useEffect(() => {
    if (isOpen && !USE_MOCK_SIGNATURE && institutions.length > 0) {
      // 약간의 딜레이를 주고 팝업 열기
      const timer = setTimeout(() => {
        handleStartSignature();
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isOpen, institutions.length, handleStartSignature]);

  // 모달 UI는 렌더링하지 않음 (팝업 관리 로직만 수행)
  // isOpen이 false면 아무것도 렌더링하지 않음
  return null;
};

export default OmniAuthModal;
